// Roguelike art pack — the ONE definitive pixel-art style shipped after
// the 2026-04-20 art overhaul. Replaces the previous 8 swappable packs
// with a single, curated look built around stocky silhouettes, 8-frame
// walk cycles, 6-frame attack anims, and 4-layer faction differentiation
// (accessory / accent / outline tint / foot-ring).
//
// Character rendering lives in ../roguelike/ sub-modules. Building /
// tile / car / earth sheets are inlined here (small, mostly palette
// adjustments on the legacy classic-pack layout).

import type { ArtPack } from '../pack';
import { EARTH_ATLAS, EARTH_COLS, EARTH_ROWS } from '../earth_atlas';
import type { Sheet, BuildingKind, CarColor, TileKind } from '../../sprites';
import { newCanvas, makeTexture, px, rect } from '../canvas_utils';
import { makeCharacterSheet } from '../roguelike/character';
import { NEUTRAL } from '../roguelike/palette';

// ---------- Buildings (48×64) ----------

const BUILDING_W = 48;
const BUILDING_H = 64;

const BUILDING_PALETTE: Record<BuildingKind, { wall: string; roof: string; trim: string; sign: string; door: string }> = {
  house:       { wall: '#d4a574', roof: '#6b2424', trim: '#a16207', sign: '#f4ebd3', door: '#5a3a20' },
  apartment:   { wall: '#8a8297', roof: '#2f2742', trim: '#1c1530', sign: '#f4ebd3', door: '#1c1530' },
  restaurant:  { wall: '#f59e0b', roof: '#7c2d12', trim: '#92400e', sign: '#f4ebd3', door: '#451a03' },
  gym:         { wall: '#10b981', roof: '#064e3b', trim: '#047857', sign: '#f4ebd3', door: '#022c22' },
  hospital:    { wall: '#f3f4f6', roof: '#dc2626', trim: '#dc2626', sign: '#dc2626', door: '#1f2937' },
  grocery:     { wall: '#84cc16', roof: '#365314', trim: '#3f6212', sign: '#f4ebd3', door: '#1a2e05' },
  retail:      { wall: '#ec4899', roof: '#831843', trim: '#9f1239', sign: '#f4ebd3', door: '#500724' },
  department:  { wall: '#a855f7', roof: '#581c87', trim: '#6b21a8', sign: '#f4ebd3', door: '#3b0764' },
  plumber:     { wall: '#0ea5e9', roof: '#0c4a6e', trim: '#075985', sign: '#f4ebd3', door: '#082f49' },
  electrician: { wall: '#facc15', roof: '#713f12', trim: '#a16207', sign: '#0b0912', door: '#422006' },
  fire:        { wall: '#dc2626', roof: '#7f1d1d', trim: '#450a0a', sign: '#f4ebd3', door: '#facc15' },
  police:      { wall: '#1e3a8a', roof: '#172554', trim: '#0c1a4d', sign: '#f4ebd3', door: '#facc15' },
  park:        { wall: '#22c55e', roof: '#15803d', trim: '#166534', sign: '#f4ebd3', door: '#14532d' },
  cityhall:    { wall: '#e7e5e4', roof: '#78716c', trim: '#44403c', sign: '#f4ebd3', door: '#44403c' },
  cafe:        { wall: '#a78bfa', roof: '#5b21b6', trim: '#6d28d9', sign: '#f4ebd3', door: '#2e1065' },
};

const SIGN_LETTERS: Record<BuildingKind, string> = {
  house: '', apartment: 'APT', restaurant: 'EAT', gym: 'GYM', hospital: '+',
  grocery: 'FOOD', retail: 'SHOP', department: 'DEPT', plumber: 'PLMB',
  electrician: 'ELEC', fire: 'FIRE', police: 'POL', park: 'PARK',
  cityhall: 'HALL', cafe: 'CAFE',
};

function makeBuildingSheet(kind: BuildingKind): Sheet {
  const sheet = newCanvas(BUILDING_W, BUILDING_H);
  const ctx = sheet.ctx;
  const p = BUILDING_PALETTE[kind];

  // Ground shadow
  rect(ctx, 2, BUILDING_H - 3, BUILDING_W - 4, 3, 'rgba(11,9,18,.45)');
  // Wall
  rect(ctx, 4, 18, BUILDING_W - 8, BUILDING_H - 22, p.wall);
  rect(ctx, 4, 18, 2, BUILDING_H - 22, p.trim);
  rect(ctx, BUILDING_W - 6, 18, 2, BUILDING_H - 22, p.trim);
  // Sloped roof
  for (let i = 0; i < 12; i++) {
    rect(ctx, 4 + i, 18 - i, BUILDING_W - 8 - i * 2, 1, p.roof);
  }
  rect(ctx, 4, 18, BUILDING_W - 8, 2, p.roof);
  // Door
  rect(ctx, BUILDING_W / 2 - 3, BUILDING_H - 14, 6, 10, p.door);
  rect(ctx, BUILDING_W / 2 - 3, BUILDING_H - 14, 1, 10, NEUTRAL.ink);
  rect(ctx, BUILDING_W / 2 + 2, BUILDING_H - 14, 1, 10, NEUTRAL.ink);
  rect(ctx, BUILDING_W / 2 - 3, BUILDING_H - 14, 6, 1, NEUTRAL.ink);
  px(ctx, BUILDING_W / 2 + 1, BUILDING_H - 9, '#facc15');
  // Windows
  const winY = 24;
  rect(ctx, 8, winY, 6, 6, '#bae6fd');
  rect(ctx, 8, winY, 6, 1, '#0c4a6e');
  rect(ctx, 8, winY + 5, 6, 1, '#0c4a6e');
  rect(ctx, BUILDING_W - 14, winY, 6, 6, '#bae6fd');
  rect(ctx, BUILDING_W - 14, winY, 6, 1, '#0c4a6e');
  rect(ctx, BUILDING_W - 14, winY + 5, 6, 1, '#0c4a6e');
  // Sign
  const label = SIGN_LETTERS[kind];
  if (label) {
    rect(ctx, 6, 12, BUILDING_W - 12, 6, NEUTRAL.darkSlate);
    ctx.fillStyle = p.sign;
    ctx.font = '6px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, BUILDING_W / 2, 16);
  }
  // Park override
  if (kind === 'park') {
    ctx.clearRect(0, 0, BUILDING_W, BUILDING_H);
    rect(ctx, 0, BUILDING_H - 8, BUILDING_W, 8, '#22c55e');
    rect(ctx, BUILDING_W / 2 - 1, BUILDING_H - 24, 2, 16, '#5a3a20');
    rect(ctx, BUILDING_W / 2 - 6, BUILDING_H - 38, 12, 14, '#15803d');
    rect(ctx, BUILDING_W / 2 - 4, BUILDING_H - 42, 8, 6, '#22c55e');
    rect(ctx, 6, BUILDING_H - 10, 10, 2, '#5a3a20');
    rect(ctx, 6, BUILDING_H - 10, 1, 4, '#5a3a20');
    rect(ctx, 15, BUILDING_H - 10, 1, 4, '#5a3a20');
  }

  return { texture: makeTexture(sheet.c), cols: 1, rows: 1, frameW: BUILDING_W, frameH: BUILDING_H };
}

// ---------- Cars ----------

const CAR_W = 14;
const CAR_H = 24;

const CAR_PALETTE: Record<CarColor, { body: string; trim: string; window: string }> = {
  red:    { body: '#dc2626', trim: '#7f1d1d', window: '#94d2ff' },
  blue:   { body: '#3b82f6', trim: '#1e3a8a', window: '#94d2ff' },
  yellow: { body: '#facc15', trim: '#a16207', window: '#94d2ff' },
  black:  { body: '#27272a', trim: '#0a0a0a', window: '#94d2ff' },
  taxi:   { body: '#fde047', trim: '#a16207', window: '#94d2ff' },
};

function makeCarSheet(color: CarColor): Sheet {
  const sheet = newCanvas(CAR_W, CAR_H);
  const ctx = sheet.ctx;
  const p = CAR_PALETTE[color];

  rect(ctx, 2, 2, CAR_W - 4, CAR_H - 4, p.body);
  rect(ctx, 2, 2, CAR_W - 4, 1, p.trim);
  rect(ctx, 2, CAR_H - 3, CAR_W - 4, 1, p.trim);
  rect(ctx, 2, 2, 1, CAR_H - 4, p.trim);
  rect(ctx, CAR_W - 3, 2, 1, CAR_H - 4, p.trim);
  rect(ctx, 3, 4, CAR_W - 6, 4, p.window);
  rect(ctx, 4, 9, CAR_W - 8, 6, p.trim);
  rect(ctx, 3, 16, CAR_W - 6, 3, p.window);
  rect(ctx, 0, 5, 2, 3, NEUTRAL.ink);
  rect(ctx, CAR_W - 2, 5, 2, 3, NEUTRAL.ink);
  rect(ctx, 0, CAR_H - 8, 2, 3, NEUTRAL.ink);
  rect(ctx, CAR_W - 2, CAR_H - 8, 2, 3, NEUTRAL.ink);
  rect(ctx, 3, 1, 2, 1, '#fff8a0');
  rect(ctx, CAR_W - 5, 1, 2, 1, '#fff8a0');
  if (color === 'taxi') {
    rect(ctx, 4, 10, CAR_W - 8, 4, NEUTRAL.ink);
    ctx.fillStyle = '#fde047';
    ctx.font = '5px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('TAXI', CAR_W / 2, 12);
  }

  return { texture: makeTexture(sheet.c), cols: 1, rows: 1, frameW: CAR_W, frameH: CAR_H };
}

// ---------- Tiles ----------

const TILE_SIZE = 16;

function makeTileSheet(): { texture: import('three').Texture; tiles: Record<TileKind, { u: number; v: number }> } {
  const kinds: TileKind[] = ['grass', 'road', 'sidewalk', 'plaza', 'dirt', 'water'];
  const cols = kinds.length;
  const sheet = newCanvas(TILE_SIZE * cols, TILE_SIZE);
  const ctx = sheet.ctx;
  const tiles: Record<TileKind, { u: number; v: number }> = {} as Record<TileKind, { u: number; v: number }>;

  const palettes: Record<TileKind, [string, string, string]> = {
    grass:    ['#4a7c30', '#3d6a26', '#5b8f3c'],
    road:     ['#3f3f46', '#27272a', '#52525b'],
    sidewalk: ['#a1a1aa', '#71717a', '#d4d4d8'],
    plaza:    ['#cbb89d', '#a89878', '#d8c8a8'],
    dirt:     ['#7c5a3a', '#5a3f25', '#92704a'],
    water:    ['#3b82f6', '#1d4ed8', '#60a5fa'],
  };

  kinds.forEach((kind, i) => {
    const ox = i * TILE_SIZE;
    const [base, dark, light] = palettes[kind];
    rect(ctx, ox, 0, TILE_SIZE, TILE_SIZE, base);
    for (let y = 0; y < TILE_SIZE; y++) {
      for (let x = 0; x < TILE_SIZE; x++) {
        const r = (x * 374761 + y * 668265) ^ (i * 13);
        if ((r & 7) === 0) px(ctx, ox + x, y, dark);
        else if ((r & 13) === 0) px(ctx, ox + x, y, light);
      }
    }
    if (kind === 'road') {
      for (let y = 1; y < TILE_SIZE; y += 4) {
        rect(ctx, ox + 7, y, 2, 2, '#facc15');
      }
    }
    if (kind === 'sidewalk') {
      rect(ctx, ox, 0, TILE_SIZE, 1, dark);
      rect(ctx, ox, TILE_SIZE - 1, TILE_SIZE, 1, dark);
    }
    tiles[kind] = { u: i / cols, v: 0 };
  });

  return { texture: makeTexture(sheet.c), tiles };
}

// ---------- Matchmaker Earth map ----------

function drawPixelatedEarth(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;

  const g = ctx.createLinearGradient(0, 0, 0, canvas.height);
  g.addColorStop(0, '#0c1a2e');
  g.addColorStop(1, '#071226');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.fillStyle = 'rgba(148,163,184,.3)';
  for (let i = 0; i < 60; i++) {
    const x = Math.floor(Math.random() * canvas.width);
    const y = Math.floor(Math.random() * canvas.height);
    ctx.fillRect(x, y, 2, 2);
  }

  const atlas = EARTH_ATLAS;
  const CELL = Math.floor(canvas.width / EARTH_COLS);
  const landShades = ['#1a2e1a', '#16351c', '#1d3a22', '#224226', '#275029'];
  const mountainShades = ['#3b4f2d', '#4b5f32', '#5a6c3a'];
  for (let r = 0; r < EARTH_ROWS; r++) {
    for (let c = 0; c < EARTH_COLS; c++) {
      const ch = atlas[r][c];
      if (ch === '1') {
        const shade = (r * 7 + c * 3) % landShades.length;
        ctx.fillStyle = landShades[shade];
        ctx.fillRect(c * CELL, r * CELL, CELL, CELL);
      } else if (ch === '2') {
        const shade = (r * 5 + c * 2) % mountainShades.length;
        ctx.fillStyle = mountainShades[shade];
        ctx.fillRect(c * CELL, r * CELL, CELL, CELL);
      }
    }
  }

  ctx.fillStyle = 'rgba(56,189,248,.05)';
  for (let y = 0; y < canvas.height; y += 3) {
    ctx.fillRect(0, y, canvas.width, 1);
  }
}

// ---------- Pack export ----------

export const roguelikePack: ArtPack = {
  id: 'roguelike',
  name: 'Roguelike',
  tagline: 'Stocky silhouettes, smooth 8-frame gait, distinct Virgin/Chad reads.',
  pixelScale: 2,
  makeCharacterSheet,
  makeBuildingSheet,
  makeCarSheet,
  makeTileSheet,
  drawPixelatedEarth,
};
